import DataBusDefaults from "@/services/DataBusDefaults";
import { useEffect, useState } from "react";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
import { Modal, Nav } from "rsuite";
import CDNLink from "../../../../../components/CDNLink/CDNLink";
import PDFViewer, {
  PDFHighlight,
} from "../../../../../components/PDFViewer/PDFViewer";
import DebugDataComponent from "../../../../../debug/DebugDataComponent";
import i18n from "../../../../../i18n";
import BFDropdown from "../../../../../modules/abstract-ui/general/Dropdown/BFDropdown";
import ExpressionHelper from "../../../../../modules/generic-forms/util/ExpressionHelper";
import { useViewportSelector } from "../../../../../redux/hooks";
import { Invoice, InvoiceKind } from "../../RAInterfaces";
import RADeclineInvoiceForm from "../DeclineInvoice/RADeclineInvoiceForm";
import { RADocumentsView } from "../Documents/RADocumentsView";
import InvoiceResubmissionView from "../InvoiceResubmissions/InvoiceResubmissionView";
import {
  RAAccountingView,
  RACommentsView,
  RAHistoryView,
  RAPaymentsView,
} from "../RAInvoiceDetails";
import "./RAInvoiceDetailsComponent.scss";
import RAInvoiceDetailsMain from "./RAInvoiceDetailsMain";

type TabType =
  | "details"
  | "pdf"
  | "comments"
  | "history"
  | "payments"
  | "accounting"
  | "documents";
interface Props {
  invoice: Invoice;
  editable?: boolean;
  card?: boolean;
}

const RAInvoiceDetailsComponent = (props: Props) => {
  const match = useRouteMatch();
  const [declineModal, setDeclineModal] = useState<boolean>(false);
  const viewportSelector = useViewportSelector();
  const [active, setActive] = useState<TabType>("details");
  const isSplitView = ["xxl", "xl", "lg"].indexOf(viewportSelector) !== -1;

  const [highlights, setHighlights] = useState<PDFHighlight[]>([]);

  useEffect(() => {
    if (active === "pdf" && isSplitView) {
      setActive("details");
    }
  }, [isSplitView, active]);

  const fileCdnData = props.invoice?.data?.invoice?.file || {};

  return (
    <div
      className={`ra-invoice-details-component ${
        isSplitView ? "split-view" : ""
      } ${props.card ? "__card" : ""}`}
    >
      <div className="invoice-details">
        <div className={`nav-container`}>
          <Route
            path={`${match.path}/:active?`}
            render={(params) => (
              <Nav
                appearance="subtle"
                activeKey={params.match.params.active}
                onSelect={(eventKey) => {
                  DataBusDefaults.route({
                    route: `${match.url}/${eventKey}`,
                    append: false,
                  });
                }}
              >
                <Nav.Item eventKey="details">
                  {i18n.t("ra:InvoiceDetails.Tabs.Details")}
                </Nav.Item>
                {!isSplitView && (
                  <Nav.Item eventKey="pdf">
                    {i18n.t("ra:InvoiceDetails.Tabs.PDF")}
                  </Nav.Item>
                )}
                <Nav.Item eventKey="comments">
                  {i18n.t("ra:InvoiceDetails.Tabs.Comment")}
                  {props.invoice?.communication?.numComments > 0 &&
                    `(${props.invoice?.communication?.numComments})`}
                </Nav.Item>
                <Nav.Item eventKey="history">
                  {i18n.t("ra:InvoiceDetails.Tabs.History")}
                </Nav.Item>
                <Nav.Item eventKey="accounting">
                  {i18n.t("ra:InvoiceDetails.Tabs.Accounting", "Buchhaltung")}
                </Nav.Item>
                <Nav.Item eventKey="payments">
                  {i18n.t("ra:InvoiceDetails.Tabs.Payments", "Zahlungen")}
                </Nav.Item>
                <Nav.Item eventKey="documents">
                  {i18n.t("ra:InvoiceDetails.Tabs.Documents", "Dokumente")} (
                  {(props.invoice?.data?.attachments || []).length})
                </Nav.Item>
                <Nav.Item eventKey="resubmissions">
                  {i18n.t(
                    "ra:InvoiceDetails.Tabs.Resubmissions",
                    "Wiedervorlagen"
                  )}
                </Nav.Item>
              </Nav>
            )}
          />
          <div className={`action`}>
            <BFDropdown
              appearance="clear"
              label={i18n.t("ra:InvoiceDetails.Action", "Aktion")}
              items={[
                {
                  disabled:
                    props.invoice?.data?.status === "init" ||
                    (props.invoice?.data.direction === "INCOMING" &&
                      props.invoice?.data.kind === InvoiceKind.STORNO) ||
                    props.invoice?.data?.paid ||
                    props.invoice?.data?.status === "declined",
                  className: `decline-button`,
                  type: "button",
                  text: i18n.t(
                    "ra:InvoiceDetails.DeclineInvoice",
                    "Rechnung ablehnen"
                  ),
                  onSelect: () => {
                    setDeclineModal(true);
                  },
                },
              ]}
            />
          </div>
        </div>
        <DebugDataComponent data={props.invoice} />
        <div className="content">
          <Switch>
            <Route
              path={`${match.path}/details`}
              render={() => (
                <RAInvoiceDetailsMain
                  invoice={props.invoice}
                  highlights={highlights}
                  setHighlights={setHighlights}
                  reloadList={() => {
                    // Implement the reload logic here
                  }}
                />
              )}
            />
            <Route
              path={`${match.path}/comments`}
              render={() => <RACommentsView invoice={props.invoice} />}
            />
            <Route
              path={`${match.path}/history`}
              render={() => <RAHistoryView invoice={props.invoice} />}
            />
            <Route
              path={`${match.path}/payments`}
              render={() => <RAPaymentsView invoice={props.invoice} />}
            />
            <Route
              path={`${match.path}/documents`}
              render={() => <RADocumentsView invoice={props.invoice} />}
            />
            <Route
              path={`${match.path}/accounting`}
              render={() => <RAAccountingView {...props} />}
            />
            <Route
              path={`${match.path}/resubmissions`}
              render={() => <InvoiceResubmissionView invoice={props.invoice} />}
            />
            <Route render={() => <Redirect to={`${match.url}/details`} />} />
          </Switch>
        </div>
      </div>

      {isSplitView && (
        <div className="invoice-pdf">
          {fileCdnData && (
            <CDNLink
              assetType={"invoice"}
              assetId={props.invoice?._id}
              assetField={"data.invoice.file"}
              cdnId={fileCdnData.id}
              filename={fileCdnData.filename}
              fileKey={fileCdnData.key}
              hasFolderReadPermissions={true}
              render={(url) => (
                <PDFViewer
                  zoomFactorStorageKey={"pdf_zoom_factor_modal_view"}
                  url={url}
                  highlights={highlights}
                  filename={fileCdnData.filename}
                />
              )}
            />
          )}
          <div className={`inset-shadow`} />
        </div>
      )}

      <Modal
        size={"lg"}
        open={declineModal}
        className="ra-book-invoice-decline-modal"
      >
        <div className="modal-content">
          <RADeclineInvoiceForm
            direction={props.invoice?.data?.direction}
            kind={props.invoice?.data?.kind}
            type={props.invoice?.data?.type}
            showProcess
            task={props.invoice?.data?.status === "inApproval"}
            invoiceId={props.invoice?._id}
            onClose={() => setDeclineModal(null)}
            onFinish={() => {
              setDeclineModal(false);

              // TODO_INVOICE reload after DECLINE missing
              // if (props.invoice?.intern?.invoiceTaskId) {
              //   CacheService.getData({
              //     oType: "asset",
              //     assetType: "task",
              //     forceReload: true,
              //     id: props.invoice?.intern?.invoiceTaskId,
              //   });
              // }
            }}
          />
        </div>
      </Modal>
    </div>
  );
};
export default RAInvoiceDetailsComponent;

interface ByConfigProps {
  _invoice: string;
  _editable?: string;
  editable?: boolean;
  params: any;
}
export const RAInvoiceDetailsComponentByConfig = (props: ByConfigProps) => {
  const invoice = ExpressionHelper.evaluateExpression(
    props._invoice,
    props.params
  );
  const editable = props._editable
    ? ExpressionHelper.evaluateExpression(props._editable, props.params)
    : props.editable;
  return <RAInvoiceDetailsComponent invoice={invoice} editable={editable} />;
};
